import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { isMsisdnValid, formatMsisdn } from '@/functions/isMsisdnValid'
import generateExtRef from '@/functions/extRef'
import { injectEvinaScript, rearmEvina, complianceProtectTest, EVINA_TARGET } from '@/functions/evina'
import { prelanderRequested } from '@/functions/prelander'
import resolveVariant from '@/functions/variant'
import { configureTelemetry, track, sendPerf, sinceNavigation } from '@/functions/telemetry'
import TelkomPremiumService, { PremiumErrors, isMockMode } from '@/services/telkomPremiumService'
import IntegrationService from '@/services/integrationService'
import { useBrandStore } from '@/stores/brandStore'

export const Steps = {
  Checking: 'checking',
  Ready: 'ready',
  Msisdn: 'msisdn',
  Otp: 'otp',
  Subscribing: 'subscribing',
  Done: 'done'
}

// Longest the subscribe button waits for the Evina kit: its request plus gateway-load (up to 8 s,
// functions/evina.js). Past it the page fails open.
const EVINA_READY_CAP_MS = 10000

// Errors that end the attempt (error page) rather than being corrected inline.
const FATAL_ERRORS = new Set([
  PremiumErrors.ANTI_FRAUD_BLOCK,
  PremiumErrors.PARTNER_IS_NOT_PREMIUM,
  PremiumErrors.PRODUCT_IS_NOT_ONLINE,
  PremiumErrors.NETWORK,
  PremiumErrors.NO_SERVICE,
  'EVINA_RETRY'
])

function isActiveSubscriber(user) {
  if (!user) return false
  const status = String(user.statusName ?? user.Status ?? '').toUpperCase()
  return status.startsWith('ACTIVE') || Number(user.statusId ?? user.StatusId) === 21
}

export const useSubscriptionStore = defineStore('subscription', () => {
  const step = ref(Steps.Checking)
  // What the page shows: while /sub is in flight the tapped step stays on screen with a spinner on its button.
  const lastInteractive = ref(Steps.Checking)
  watch(step, (value) => {
    if (value !== Steps.Subscribing && value !== Steps.Done) lastInteractive.value = value
  })
  const screen = computed(() => (step.value === Steps.Subscribing ? lastInteractive.value : step.value))
  const msisdn = ref('')
  const headerEnriched = ref(false)
  const heIdentified = ref(false)
  const heToken = ref(null)
  // Raw outcome of the Premium /he call (or the error), for the ?debug panel.
  const heResult = ref(null)
  const otp = ref('')
  // One ext_ref per page load: it is Telkom's reference for /otp + /sub AND Evina's ti.
  const extRef = ref(generateExtRef(useBrandStore().brandConfig.extRefPrefix))
  const busy = ref(false)
  const errorCode = ref('')
  const alreadySubscribed = ref(false)
  const result = ref(null)
  // Telkom's outcome once its callback is in (/premium/result), for the portal link on the thank-you page.
  const portal = ref(null)
  const evina = ref({ armed: false, gatewayLoaded: null })
  // JOIN and CONFIRM wait for this: the Evina kit is in the page and bound, or arming ended without it
  // (failed, timed out or not used). Telkom blocks a tap Evina did not see (ft 2802/2804/3101).
  const evinaReady = ref(false)
  // The visitor left via the close cross (cptpl_exit) in the header.
  const cancelled = ref(false)
  // ?prelander=1 (functions/prelander.js): the pre-lander shows first and starts identification and the
  // Evina kit request while it runs; the subscribe panel carries on from there and injects the kit
  // (one /he call, one kit per page).
  const prelanding = ref(prelanderRequested())
  const prelanderTiming = ref(null)
  let prelanderStartedFlow = false

  // Campaign attribution for this visit (functions/getCampaignParametersFromUrl.js).
  const transactionId = ref(null)
  const clickId = ref(null)
  const googleClickId = ref(null)
  const campaignId = ref(null)
  const publisherId = ref(null)
  const subPublisherId = ref(null)
  const attributionSource = ref('')

  const isFatalError = computed(() => FATAL_ERRORS.has(errorCode.value))

  // Labels for the dashboard beacon (functions/telemetry.js), read when a batch goes.
  const brandStore = useBrandStore()
  configureTelemetry(() => {
    const brandConfig = brandStore.brandConfig
    const organic = !campaignId.value || String(campaignId.value) === String(brandConfig.organicCampaignId)
    return {
      extRef: extRef.value,
      serviceId: brandConfig.networkServiceId,
      page: resolveVariant(brandConfig),
      prelander: prelanderRequested(),
      channel: organic ? 'organic-web' : 'paid-web',
      campaignId: campaignId.value || undefined
    }
  })

  function setCampaignParameters(params) {
    transactionId.value = params?.transactionId ?? null
    clickId.value = params?.clickId ?? null
    googleClickId.value = params?.googleClickId ?? null
    campaignId.value = params?.campaignId ?? null
    publisherId.value = params?.publisherId ?? null
    subPublisherId.value = params?.subPublisherId ?? null
    attributionSource.value = params?.source ?? ''
  }

  function attribution() {
    return {
      transactionId: transactionId.value,
      clickId: clickId.value,
      googleClickId: googleClickId.value,
      campaignId: campaignId.value,
      publisherId: publisherId.value,
      subPublisherId: subPublisherId.value
    }
  }

  function numberFor(brandConfig) {
    return heIdentified.value ? '' : formatMsisdn(msisdn.value, brandConfig.msisdn)
  }

  function fail(code) {
    errorCode.value = code || PremiumErrors.NETWORK
    return { ok: false, errorCode: errorCode.value, fatal: FATAL_ERRORS.has(errorCode.value) }
  }

  // Step 1: HE decides whether the subscriber types a number. Any failure falls back to OTP.
  async function checkHeaderEnrichment(brandConfig) {
    step.value = Steps.Checking
    otp.value = ''
    errorCode.value = ''
    result.value = null
    // A brand whose Telkom SID is still pending (no networkServiceId) is identified all the same, so
    // header enrichment can be verified on its host; its subscribe then answers NO_SERVICE.
    try {
      const he = await TelkomPremiumService().checkHeaderEnrichment()
      heResult.value = he
      heIdentified.value = !!he.identified
      heToken.value = he.token || null
    } catch (error) {
      heResult.value = { error: error?.message || String(error) }
      heIdentified.value = false
      heToken.value = null
    }
    const heOutcome = heIdentified.value ? 'enriched' : heResult.value?.error ? 'error' : 'not_enriched'
    track('he', { outcome: heOutcome, ms: heResult.value?.ms ?? null })
    track('view', { onWifi: !heIdentified.value })
    step.value = heIdentified.value ? Steps.Ready : Steps.Msisdn
  }

  function useAnotherNumber() {
    heIdentified.value = false
    heToken.value = null
    errorCode.value = ''
    step.value = Steps.Msisdn
  }

  async function checkSubscription(brandConfig) {
    const number = formatMsisdn(msisdn.value, brandConfig.msisdn)
    if (!brandConfig.productId || !isMsisdnValid(number, brandConfig.msisdn)) return
    try {
      alreadySubscribed.value = isActiveSubscriber(await IntegrationService.getUser(number, brandConfig.productId))
    } catch {
      alreadySubscribed.value = false
    }
  }

  // Evina kit, loaded once per page (ti = ext_ref): on JOIN (HE) and, on the PIN flow, from the
  // number page onward (Evina, 14 Oct 2026). Mock mode skips it, except for Evina's review
  // (?mode=mock&cp=1): real kit, mocked Telkom, no charge.
  // Arming from the number page means the PIN step can arm again while the first kit is still
  // loading (gateway-load can take up to 8 s). The second call waits for the first and re-arms
  // instead of requesting or injecting another kit: Evina allows one script per page.
  let arming = null
  // The kit request starts with the page, alongside the HE check; the script is still injected only
  // after identification, as Evina requires. Saves the kit download (~95 KB gzipped) on mobile data.
  let prefetchedKit = null
  function evinaApplies(brandConfig) {
    return Boolean(brandConfig.networkServiceId) && !(isMockMode() && !complianceProtectTest())
  }

  function fetchKit(brandConfig) {
    return IntegrationService.getEvinaScript({
      extRef: extRef.value,
      serviceId: brandConfig.networkServiceId,
      msisdn: numberFor(brandConfig) || undefined,
      targetElement: EVINA_TARGET,
      cp: complianceProtectTest() || undefined
    })
  }

  function prefetchEvina(brandConfig) {
    if (prefetchedKit || !evinaApplies(brandConfig)) return
    prefetchedKit = fetchKit(brandConfig).catch(() => null)
  }

  async function armEvina(brandConfig) {
    const svcId = brandConfig.networkServiceId
    if (!svcId || (isMockMode() && !complianceProtectTest())) {
      evinaReady.value = true
      return
    }
    // A kit still loading: wait for it, then re-arm it (or load again if it failed).
    while (arming) await arming
    if (evina.value.armed) {
      rearmEvina()
      evinaReady.value = true
      return
    }
    evinaReady.value = false
    // Fail open: the button never waits longer than this, even if the kit request hangs.
    const cap = setTimeout(() => (evinaReady.value = true), EVINA_READY_CAP_MS)
    arming = loadEvina(brandConfig)
    try {
      await arming
    } finally {
      arming = null
      clearTimeout(cap)
      evinaReady.value = true
    }
  }

  function startPrelander(brandConfig) {
    prelanderStartedFlow = true
    prefetchEvina(brandConfig)
    checkHeaderEnrichment(brandConfig)
  }

  function endPrelander(timing) {
    prelanderTiming.value = timing
    prelanding.value = false
    track('prelander', { outcome: timing.capped ? 'capped' : 'advanced', ms: timing.shownMs })
  }

  // Our own pre-lander fraud check flagged the visit (functions/riskCheck.js). The pre-lander sends it
  // to the error page; JOIN never mounts, so no Evina kit goes in.
  function blockPrelander(reasons, timing) {
    prelanderTiming.value = timing
    errorCode.value = 'FRAUD_DETECTED'
    track('prelander', { outcome: 'blocked', ms: timing.shownMs })
    track('risk', { reasons })
  }

  // True once, for the subscribe panel that follows the pre-lander.
  function resumeAfterPrelander() {
    const resume = prelanderStartedFlow
    prelanderStartedFlow = false
    return resume
  }

  async function loadEvina(brandConfig) {
    const started = Date.now()
    try {
      // The kit requested with the page, if any (used once); a failed prefetch is requested again.
      const early = prefetchedKit
      prefetchedKit = null
      const kit = (early && (await early)) || (await fetchKit(brandConfig))
      if (!kit?.success || !kit?.script) return track('evina_kit', { outcome: 'failed', ms: Date.now() - started })
      const injectedAt = Date.now()
      const loaded = await injectEvinaScript(kit.script)
      evina.value = { armed: loaded.ok, gatewayLoaded: loaded.gatewayLoaded ?? null }
      track('evina_kit', { outcome: !loaded.ok ? 'failed' : loaded.gatewayLoaded ? 'loaded' : 'timeout', ms: Date.now() - injectedAt })
    } catch {
      track('evina_kit', { outcome: 'failed', ms: Date.now() - started })
      // Telkom validates on ext_ref regardless; a missing kit must not block the subscribe.
    }
  }

  // Parks transaction_id attribution under ext_ref so the Telkom callback can claim it. Best effort.
  // Not in mock mode: a mocked attempt has no Telkom subscription to attribute.
  function recordIntent(brandConfig, channel) {
    const svcId = brandConfig.networkServiceId
    if (!svcId || isMockMode()) return
    IntegrationService.recordIntent({
      serviceId: svcId,
      extRef: extRef.value,
      msisdn: numberFor(brandConfig) || undefined,
      channel,
      ...attribution()
    }).catch(() => {})
  }

  // Our own fraud check before /sub; skipped by the service unless enabled.
  async function evinaGate(brandConfig) {
    if (isMockMode()) return { allow: true }
    try {
      const verdict = await IntegrationService.evinaCheck({
        extRef: extRef.value,
        serviceId: brandConfig.networkServiceId,
        msisdn: numberFor(brandConfig) || undefined
      })
      if (verdict?.allow === false) return { allow: false, outcome: verdict.outcome }
    } catch {
      // fail open
    }
    return { allow: true }
  }

  // Step 2 (typed number only): Telkom texts the OTP; the page never sees it.
  async function requestOtp(brandConfig) {
    if (busy.value) return { ok: false }
    errorCode.value = ''
    const number = formatMsisdn(msisdn.value, brandConfig.msisdn)
    if (!isMsisdnValid(number, brandConfig.msisdn)) return { ok: false, errorCode: 'invalidNumber' }
    const svcId = brandConfig.networkServiceId
    if (!svcId) return fail(PremiumErrors.NO_SERVICE)

    busy.value = true
    try {
      recordIntent(brandConfig, 'WAP')
      const response = await TelkomPremiumService().requestOtp(svcId, extRef.value, number)
      if (!response.ok) {
        if (response.errorCode === PremiumErrors.OPTIN_ALREADY_ACTIVE) {
          alreadySubscribed.value = true
          step.value = Steps.Done
          return { ok: true, alreadySubscribed: true }
        }
        track('pin', { ok: false })
        return fail(response.errorCode)
      }
      track('pin', { ok: true })
      otp.value = ''
      step.value = Steps.Otp
      return { ok: true }
    } catch {
      track('pin', { ok: false })
      return fail(PremiumErrors.NETWORK)
    } finally {
      busy.value = false
    }
  }

  // Step 3: one call for both flows; HE sends {} (+ x=<token>), OTP sends the number and code.
  async function subscribe(brandConfig) {
    if (busy.value) return { ok: false }
    errorCode.value = ''
    const svcId = brandConfig.networkServiceId
    if (!svcId) return fail(PremiumErrors.NO_SERVICE)

    const number = numberFor(brandConfig)
    const body = heIdentified.value ? {} : { msisdnOtp: number, otp: otp.value }
    if (!heIdentified.value && !/^\d{4}$/.test(otp.value)) return fail(PremiumErrors.INVALID_OTP)

    busy.value = true
    const previousStep = step.value
    step.value = Steps.Subscribing
    // For the dashboard beacon: Telkom's answer (or why we did not ask), reported once below.
    const started = Date.now()
    let outcome = 'NETWORK'
    sendPerf()
    try {
      if (heIdentified.value) recordIntent(brandConfig, 'HE')

      const gate = await evinaGate(brandConfig)
      if (!gate.allow) {
        step.value = previousStep
        outcome = gate.outcome === 'retry' ? 'EVINA_RETRY' : 'EVINA_BLOCK'
        return fail(gate.outcome === 'retry' ? 'EVINA_RETRY' : PremiumErrors.ANTI_FRAUD_BLOCK)
      }

      const response = await TelkomPremiumService().subscribe(svcId, extRef.value, body, heToken.value)
      outcome = response.ok ? 'OK' : response.errorCode
      if (!response.ok) {
        if (response.errorCode === PremiumErrors.OPTIN_ALREADY_ACTIVE) {
          alreadySubscribed.value = true
          step.value = Steps.Done
          return { ok: true, alreadySubscribed: true }
        }
        step.value = response.errorCode === PremiumErrors.INVALID_OTP ? Steps.Otp : previousStep
        return fail(response.errorCode)
      }

      result.value = { transactionId: response.transactionId, subscriptionId: response.subscriptionId }
      recordWithIntegrationService(brandConfig, number)
      step.value = Steps.Done
      return { ok: true }
    } catch {
      step.value = previousStep
      outcome = 'NETWORK'
      return fail(PremiumErrors.NETWORK)
    } finally {
      busy.value = false
      track('subscribe', { code: outcome, ms: Date.now() - started })
    }
  }

  // Close cross (compliance exit): leaves without subscribing. A brand cancelUrl takes the visitor
  // away; otherwise the page says so and nothing is sent anywhere.
  function cancel(brandConfig) {
    if (brandConfig.cancelUrl) {
      window.location.assign(brandConfig.cancelUrl)
      return
    }
    if (prelanding.value) track('prelander', { outcome: 'exited', ms: sinceNavigation() })
    prelanding.value = false
    cancelled.value = true
  }

  // The portal link carries what Telkom's welcome SMS carries (result, subscription_id, msisdn, ext_ref).
  // In the HE flow the page knows only the ext_ref; the service has the rest once Telkom's ACTIVE
  // callback is in, usually a couple of seconds after /sub. Poll for it; the link stays usable throughout.
  let portalLookup = null
  function loadPortalResult(brandConfig, { attempts = 12, intervalMs = 1500 } = {}) {
    if (portalLookup) return portalLookup
    if (!brandConfig.networkServiceId || isMockMode() || alreadySubscribed.value) return Promise.resolve(null)
    portalLookup = (async () => {
      for (let i = 0; i < attempts && !portal.value; i++) {
        try {
          const answer = await IntegrationService.getPremiumResult({ serviceId: brandConfig.networkServiceId, extRef: extRef.value })
          if (answer?.ready) {
            portal.value = answer
            addEntryToken(brandConfig, answer)
          }
        } catch {
          // not reachable: keep trying until the attempts run out
        }
        if (!portal.value) await new Promise((resolve) => setTimeout(resolve, intervalMs))
      }
      return portal.value
    })()
    return portalLookup
  }

  function addEntryToken(brandConfig, answer) {
    if (!brandConfig.entryToken || !answer.subscriptionId) return
    IntegrationService.getEntryToken({
      serviceId: brandConfig.networkServiceId,
      extRef: extRef.value,
      subscriptionId: answer.subscriptionId
    })
      .then((minted) => {
        if (minted?.success && typeof minted.entryToken === 'string' && portal.value?.subscriptionId === answer.subscriptionId) {
          portal.value = { ...answer, entryToken: minted.entryToken }
        }
      })
      .catch(() => {})
  }

  // Best effort: the Telkom callback is the source of truth, this just gets the row in early.
  function recordWithIntegrationService(brandConfig, number) {
    if (!brandConfig.networkServiceId || !result.value?.subscriptionId || isMockMode()) return
    IntegrationService.recordPremiumSubscription({
      ...attribution(),
      serviceId: brandConfig.networkServiceId,
      subscriptionId: result.value.subscriptionId,
      // Telkom's transaction id for this /sub (the affiliate transaction_id travelled with /intent).
      transactionId: result.value.transactionId,
      extRef: extRef.value,
      msisdn: number || formatMsisdn(msisdn.value, brandConfig.msisdn) || undefined,
      channel: heIdentified.value ? 'HE' : 'WAP'
    }).catch(() => {})
  }

  return {
    Steps,
    step,
    screen,
    msisdn,
    headerEnriched,
    heIdentified,
    heToken,
    heResult,
    otp,
    extRef,
    busy,
    errorCode,
    isFatalError,
    alreadySubscribed,
    result,
    portal,
    evina,
    evinaReady,
    cancelled,
    prelanding,
    prelanderTiming,
    transactionId,
    clickId,
    googleClickId,
    campaignId,
    publisherId,
    subPublisherId,
    attributionSource,
    setCampaignParameters,
    checkHeaderEnrichment,
    useAnotherNumber,
    checkSubscription,
    armEvina,
    startPrelander,
    endPrelander,
    blockPrelander,
    resumeAfterPrelander,
    loadPortalResult,
    prefetchEvina,
    requestOtp,
    subscribe,
    cancel
  }
})
